import { z } from 'zod';
import { AspectRatio, AspectRatioFraction } from '@/model/common/image-common/aspectRatio.enum';
import { ImageMimeType } from '@/model/common/image-common/imageMimeType.enum';
import { SchoolImageVariant } from '@/model/School/SchoolImageVariant.enum';

export class SchoolImage {
  public url: string;
  /**
   * The variant of the image.
   * This tells us what are the intended use of the image.
   */
  public variant: SchoolImageVariant;
  public mimeType: ImageMimeType;
  public altText?: string;
  public width: number;
  public height: number;
  /**
   * The aspect ratio name.
   */
  public aspectRatio: AspectRatio;
  /**
   * The fraction form of the aspect ratio.
   */
  public aspectRatioFraction: AspectRatioFraction;

  constructor(json: unknown) {
    const validatedJson = SchoolImage.zodSchema.parse(json);
    this.url = validatedJson.url;
    this.variant = validatedJson.variant;
    this.mimeType = validatedJson.mimeType;
    this.altText = validatedJson.altText;
    this.width = validatedJson.width;
    this.height = validatedJson.height;
    this.aspectRatio = validatedJson.aspectRatio;
    this.aspectRatioFraction = validatedJson.aspectRatioFraction;
  }

  public static zodSchema = z.object({
    url: z.string().url(),
    variant: z.nativeEnum(SchoolImageVariant),
    mimeType: z.nativeEnum(ImageMimeType),
    altText: z.string().optional(),
    width: z.number(),
    height: z.number(),
    aspectRatio: z.nativeEnum(AspectRatio),
    aspectRatioFraction: z.nativeEnum(AspectRatioFraction),
  });

  public static fromJson(json: unknown): SchoolImage {
    const validatedJson = this.check(json);
    const school = new SchoolImage(validatedJson);
    return school;
  }

  public static fromJsonArray(jsonArray: unknown): SchoolImage[] {
    const validatedJsonArray = this.checkArray(jsonArray);
    return validatedJsonArray.map((json) => new SchoolImage(json));
  }

  public static check(json: unknown): z.infer<typeof this.zodSchema> {
    return this.zodSchema.parse(json);
  }

  public static checkArray(jsonArray: unknown): z.infer<typeof this.zodSchema>[] {
    return z.array(this.zodSchema).parse(jsonArray);
  }
}
